import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { HEADER_NAV_TREE_MQ, placeHeaderNavTree } from '@theme/mega-menu-three-column';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @param {HTMLAnchorElement} link
 * @returns {string}
 */
function treeLinkLabel(link) {
  const titled = link.querySelector('.mega-menu__link-title');
  if (titled) return titled.textContent.trim();

  for (const node of link.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
      return node.textContent.trim();
    }
  }

  return link.textContent.replace(/\s+/g, ' ').trim();
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {WeakMap<HTMLElement, number>} */
  #treePanelCloseGen = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('toggle', this.#syncNestedMenuOpen, true);
    this.addEventListener('click', this.#onTreeClick, true);
    this.#setupAnimatedElementListeners();
    placeHeaderNavTree();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#syncNestedMenuOpen, true);
    this.removeEventListener('click', this.#onTreeClick, true);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#closeDeepestTreePanel()) return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Submenu visibility uses .menu-open, not [open] alone.
   * Keep open() unchanged; only sync the class when a nested details toggles.
   * @param {Event} event
   */
  #syncNestedMenuOpen = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (details === this.refs.details) return;
    if (!details.classList.contains('menu-drawer__menu-container')) return;
    if (details.closest('accordion-custom')) return;

    if (details.open) {
      this.#syncDrillHeader();
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
      });
      return;
    }

    details.classList.remove('menu-open');
  };

  /**
   * Nested mega-tree: L1/L2 with children open the child <ul> instead of navigating.
   * @param {Event} event
   */
  #onTreeClick = (event) => {
    if (window.matchMedia(HEADER_NAV_TREE_MQ).matches) return;
    if (!(event.target instanceof Element)) return;
    if (
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
    ) {
      return;
    }

    const link = event.target.closest('[data-header-nav-tree] a');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    if (link.classList.contains('mega-menu__view-all') || link.classList.contains('menu-drawer__view-all-category')) {
      return;
    }

    const panel = link.nextElementSibling;
    if (!(panel instanceof HTMLUListElement)) return;

    event.preventDefault();

    const parentList = link.parentElement?.parentElement;
    if (parentList instanceof HTMLElement) {
      parentList.querySelectorAll(':scope > li > ul.is-open, :scope > li > ul[data-tree-open]').forEach((open) => {
        if (open === panel || !(open instanceof HTMLElement)) return;
        this.#abandonTreePanel(open);
      });
    }

    const gen = (this.#treePanelCloseGen.get(panel) || 0) + 1;
    this.#treePanelCloseGen.set(panel, gen);
    panel.hidden = false;
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.dataset.treeOpen = 'true';
    panel.classList.add('is-open');
    link.classList.add('active');
    link.setAttribute('aria-expanded', 'true');
    this.#syncDrillHeader();
    requestAnimationFrame(() => {
      if (this.#treePanelCloseGen.get(panel) !== gen) return;
      panel.classList.add('menu-open');
    });
  };

  /**
   * @param {HTMLElement} panel
   */
  #abandonTreePanel(panel) {
    const gen = (this.#treePanelCloseGen.get(panel) || 0) + 1;
    this.#treePanelCloseGen.set(panel, gen);
    panel.classList.remove('is-open', 'menu-open');
    delete panel.dataset.treeOpen;
    panel.hidden = true;
    panel.style.display = 'none';
    const parentLink = panel.parentElement?.querySelector(':scope > a[data-has-children]');
    parentLink?.classList.remove('active');
    parentLink?.removeAttribute('aria-expanded');
  }

  /**
   * Live: sticky header toont de huidige categorie; banner alleen op Categorieën-L1.
   */
  #syncDrillHeader() {
    const header = this.querySelector('.menu-drawer__submenu-header');
    const label = header?.querySelector('.menu-drawer__back-button .menu-drawer__menu-item-text');
    if (!(header instanceof HTMLElement) || !(label instanceof HTMLElement)) return;

    if (!label.dataset.drawerRootTitle) {
      label.dataset.drawerRootTitle = label.textContent.trim();
    }

    const openPanels = [
      ...this.querySelectorAll('[data-header-nav-tree] ul.is-open, [data-header-nav-tree] ul[data-tree-open]'),
    ];
    const deepest = openPanels.at(-1);
    if (deepest instanceof HTMLElement) {
      const parentLink = deepest.parentElement?.querySelector(':scope > a[data-has-children]');
      label.textContent =
        parentLink instanceof HTMLAnchorElement ? treeLinkLabel(parentLink) : label.dataset.drawerRootTitle;
      header.classList.add('is-drilled-in');
      return;
    }

    label.textContent = label.dataset.drawerRootTitle;
    header.classList.remove('is-drilled-in');
  }

  /**
   * @returns {boolean}
   */
  #closeDeepestTreePanel() {
    const openPanels = [
      ...this.querySelectorAll('[data-header-nav-tree] ul.is-open, [data-header-nav-tree] ul[data-tree-open]'),
    ];
    const panel = openPanels.at(-1);
    if (!(panel instanceof HTMLElement)) return false;

    const parentLink = panel.parentElement?.querySelector(':scope > a[data-has-children]');
    const gen = (this.#treePanelCloseGen.get(panel) || 0) + 1;
    this.#treePanelCloseGen.set(panel, gen);

    panel.classList.remove('is-open', 'menu-open');
    delete panel.dataset.treeOpen;
    parentLink?.classList.remove('active');
    parentLink?.removeAttribute('aria-expanded');
    this.#syncDrillHeader();

    const hidePanel = () => {
      if (this.#treePanelCloseGen.get(panel) !== gen) return;
      if (panel.classList.contains('is-open') || panel.dataset.treeOpen === 'true') return;
      panel.hidden = true;
      panel.style.display = 'none';
    };

    if (typeof panel.getAnimations === 'function') {
      onAnimationEnd(panel, hidePanel);
    } else {
      hidePanel();
    }

    return true;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Native <details> togglet `open` tijdens de click; zonder preventDefault
    // zou isOpen hier al true zijn en close() i.p.v. open() aanroepen.
    event?.preventDefault?.();

    if (!this.isOpen) return this.open(event);
    return this.close();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    // Zelfde als toggle(): native <details> togglet ná deze handler.
    // Zonder preventDefault gaat [open] meteen weer dicht → Categorieën lijkt dood.
    event?.preventDefault?.();

    placeHeaderNavTree();
    this.#reveal(this.#getDetailsElement(event));
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #reveal(details) {
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      const innerSubmenu = details.querySelector(':scope > .menu-drawer__submenu .menu-drawer__inner-submenu');
      if (innerSubmenu instanceof HTMLElement) {
        innerSubmenu.scrollTop = 0;
      }
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    if (this.#closeDeepestTreePanel()) return;
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.querySelectorAll('[data-header-nav-tree] ul.is-open, [data-header-nav-tree] ul[data-tree-open]').forEach(
        (panel) => {
          if (!(panel instanceof HTMLElement)) return;
          this.#abandonTreePanel(panel);
        }
      );
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
